import { useEffect, useMemo, useState } from "react";

const BASE_URL = process.env.REACT_APP_URL_INFO;
const CACHE_TTL = 300_000;  
const cache = new Map();
const inflight = new Map();
const pendingKeys = new Set();

function normalizePlaces(places) {
  return [...new Set(places)].sort((a, b) => a.localeCompare(b));
}
function normalizeYears(years) {
  return [...new Set(years)].sort((a, b) => a - b);
}

function makeKey(indicatorName, places, years, mode) {
  return JSON.stringify({ indicatorName, places, years, mode });
}

async function postBatch(indicatorName, placeNames, years, mode, signal, retries = 3) {
  const path =
    mode === "sum"
      ? "/indicator-data/snipped-sum/batch"
      : "/indicator-data/snipped-average/batch";

  let lastError;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const res = await fetch(`${BASE_URL}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ indicator: indicatorName, places: placeNames, years }),
      });

      if (res.status === 404) throw new Error("batch-not-available");
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const payload = await res.json();
      const out = {};
      for (const [place, arr] of Object.entries(payload || {})) {
        out[place] = (arr || [])
          .map((p) => {
            const year = Number(p.year ?? p.date);
            const num =
              typeof p.total === "number"
                ? p.total
                : typeof p.average === "number"
                ? p.average
                : typeof p.value === "number"
                ? p.value
                : null;
            if (!Number.isFinite(year) || num == null) return null;
            return { year, value: num, average: num };
          })
          .filter(Boolean)
          .sort((a, b) => a.year - b.year);
      }
      return out;
    } catch (error) {
      lastError = error;
      if (error.message === "batch-not-available") {
        throw error;
      }
      
      if (attempt < retries) {
        const delay = Math.min(1000 * Math.pow(2, attempt), 5000);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  throw lastError;
}

async function getSingle(indicatorName, place, year, signal) {
  const url = `${BASE_URL}/indicator-data/snipped-average?ind=${encodeURIComponent(
    indicatorName
  )}&place=${encodeURIComponent(place)}&year=${year}`;

  const res = await fetch(url, { signal });
  if (!res.ok) return { place, year, average: null };

  const json = await res.json();
  const num =
    typeof json?.average === "number"
      ? json.average
      : typeof json?.value === "number"
      ? json.value
      : null;

  return { place, year, average: num, value: num };
}

async function fallbackFanout(indicatorName, placeNames, years, mode, signal) {
  if (mode === "sum") return {};

  const places = normalizePlaces(placeNames);
  const ys = normalizeYears(years);
  const tasks = [];
  for (const place of places) {
    for (const y of ys) {
      tasks.push(() => getSingle(indicatorName, place, y, signal));
    }
  }

  const CONCURRENCY = 8;
  const results = [];
  let idx = 0;

  async function worker() {
    while (idx < tasks.length) {
      const i = idx++;
      results[i] = await tasks[i]();
    }
  }
  await Promise.all(
    Array.from({ length: Math.min(CONCURRENCY, tasks.length) }, worker)
  );

  const out = {};
  for (const r of results) {
    if (!r) continue;
    const { place, year, average, value } = r;
    const num = average ?? value;
    if (num == null) continue;
    (out[place] ||= []).push({ year, average: num, value: num });
  }
  for (const p of Object.keys(out)) out[p].sort((a, b) => a.year - b.year);
  return out;
}

export function useIndicatorSeries(
  indicatorName,
  placeNames = [],
  years = [],
  mode = "average"
) {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);

  const normPlaces = useMemo(() => normalizePlaces(placeNames), [placeNames]);
  const normYears = useMemo(() => normalizeYears(years), [years]);
  const safeMode = mode === "sum" ? "sum" : "average";

  const key = useMemo(
    () => makeKey(indicatorName, normPlaces, normYears, safeMode),
    [indicatorName, normPlaces, normYears, safeMode]
  );

  useEffect(() => {
    if (!key) {
      setData({});
      setLoading(false);
      return;
    }

    const now = Date.now();
    const cached = cache.get(key);
    if (cached) {
      const age = now - cached.ts;
      const isFresh = age < CACHE_TTL;
      
      if (isFresh) {
        setData(cached.data);
        setLoading(false);
        return;
      } 
    }

    const existingPromise = inflight.get(key);
    if (existingPromise) {
      setLoading(true);
      existingPromise.then(result => {
        setData(result);
        setLoading(false);
      }).catch(err => {
        setData({});
        setLoading(false);
      });
      return;
    }

    if (pendingKeys.has(key)) {
      pendingKeys.delete(key);
    }

    let isMounted = true;

    async function run() {
      setLoading(true);
      
      pendingKeys.add(key);
      
      try {
        const p = (async () => {
          try {
            const result = await postBatch(
              indicatorName,
              normPlaces,
              normYears,
              safeMode,
              null 
            );
            return result;
          } catch (e) {
            if (e.message === "batch-not-available") {
              return await fallbackFanout(
                indicatorName,
                normPlaces,
                normYears,
                safeMode,
                null
              );
            }
            throw e;
          } finally {
            inflight.delete(key);
            pendingKeys.delete(key);
          }
        })();

        inflight.set(key, p);
        const result = await p;
        
        cache.set(key, { data: result, ts: Date.now() });
        
        if (isMounted) {
          setData(result);
        } 
      } catch (err) {
        if (isMounted) {
          setData({});
        }
        pendingKeys.delete(key);
        inflight.delete(key);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    run();

    return () => {
      isMounted = false;
    };
  }, [key, indicatorName, safeMode, normPlaces, normYears]);

  return { data, loading };
}
